/**
 * 집 스케치 영상 — v2 문서 버전.
 *
 * 프로덕션(`SketchVideoStepAdapter`)은 v1 문서 두 장(안내 → 전체화면 녹화)을 코드에
 * 들고 있고, 어댑터의 `onComplete` 가 업로드 3단(토큰 발급 → 스토리지 업로드 →
 * 세션 완료)을 돌린다. 화면은 문서인데 그 뒤가 코드라, 문서만 봐서는 촬영한 영상이
 * 어디로 가는지 알 수 없었다.
 *
 * 화면은 그대로 옮긴다 — 같은 `video-recorder` 입력을 같은 옵션으로 쓰므로 촬영 UI 는
 * 프로덕션과 동일하다. 달라지는 건 업로드가 문서에 적힌다는 것뿐이다.
 *
 * 업로드를 세 개의 `callAction` 으로 나눈 이유: 재개 가능 업로드(`RESUMABLE`) 전환처럼
 * 중간 한 단계만 바뀌는 변경이 실제로 있었다. 한 덩어리로 두면 그 변경이 문서 밖에서
 * 조용히 일어난다.
 */
import type { AssetDocument } from "@miso/design-system";

import { STAGE_DOCUMENT_IDS } from "./counselFullFlow";

/** 영상 질문 id. 업로드 액션이 이 값을 읽는다. */
export const SKETCH_VIDEO_QUESTION_ID = "sketchVideo";

const doc: AssetDocument = {
  id: STAGE_DOCUMENT_IDS.sketchVideo,
  schemaVersion: 2,
  initial: "cover",
  steps: {
    cover: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: { variant: "cover" },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: { label: "촬영하기" },
              on: { onPress: [{ do: "submit" }] },
            },
          ],
        },
        children: [
          {
            type: "Cover",
            props: {
              emoji: "🏠",
              emojiLabel: "집",
              title: "집 스케치",
              description: "집 스케치 영상 촬영하기",
            },
          },
        ],
      },
      next: { kind: "goto", target: "record" },
    },

    record: {
      kind: "screen",
      layout: {
        type: "QuestionSequence",
        children: [
          {
            type: "Question",
            bind: SKETCH_VIDEO_QUESTION_ID,
            props: {
              layout: "video-full",
              appBarTitle: "집 스케치 영상",
              title: "집 스케치 영상",
            },
            children: [
              {
                type: "Input.video-recorder",
                bind: SKETCH_VIDEO_QUESTION_ID,
                props: {
                  required: true,
                  preferUltraWide: true,
                  defaultFacingMode: "environment",
                  autoRequestPermission: true,
                },
              },
            ],
          },
        ],
      },
      /*
       * 촬영이 끝나면 업로드한다. 마지막 `submit` 이 있어야 전이가 일어난다 —
       * 업로드가 실패하면 화면에 남아 다시 시도할 수 있어야 하기 때문이다.
       */
      onSubmit: [
        {
          do: "callAction",
          name: "video.requestUploadToken",
          args: {
            resourceType: "VIDEO",
            subType: "VISIT_COUNSEL_VIDEO_CLIP",
            video: { $var: SKETCH_VIDEO_QUESTION_ID },
          },
          resultAs: "upload",
        },
        {
          do: "callAction",
          name: "video.upload",
          args: {
            upload: { $var: "upload" },
            video: { $var: SKETCH_VIDEO_QUESTION_ID },
          },
        },
        {
          do: "callAction",
          name: "completeSketchingSession",
          args: { videoToken: { $var: "upload.structuredToken" } },
        },
        { do: "toast", tone: "success", text: "집 스케치 영상이 등록되었습니다" },
        {
          do: "poll",
          until: { not: { isEmpty: { var: "counsel.sessions.sketching.video" } } },
          budgetMs: 10_000,
          minIntervalMs: 400,
          maxIntervalMs: 1_000,
          onTimeout: [{ do: "submit" }],
        },
        { do: "submit" },
      ],
      next: { kind: "exit" },
    },
  },
};

export const SKETCH_VIDEO = { doc };
